/* eslint-disable */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import Master from '../Master';
import IziToast from '../IziToast';
import Button from '../Buttons';

// Cap at 2dp — bare toLocaleString() with no options lets binary
// floating-point noise from upstream arithmetic leak straight into the UI.
const moneyOpts = { maximumFractionDigits: 2 };

const PaymentRedirection = ({ user }) => {
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: { Authorization: `Bearer ${userToken}`, 'Content-Type': 'application/json' },
  };

  // ── Source (FROM) section ──────────────────────────────────────────────────
  const [fromProperties, setFromProperties] = useState([]);
  const [fromPropertyId, setFromPropertyId] = useState('');
  const [fromTenants, setFromTenants] = useState([]);
  const [fromTenantId, setFromTenantId] = useState('');
  const [fromPaymentDate, setFromPaymentDate] = useState('');
  const [fromPayments, setFromPayments] = useState([]);
  const [fromPaymentId, setFromPaymentId] = useState('');
  const [fromPaymentAmount, setFromPaymentAmount] = useState(0);

  // ── Destination (TO) section ───────────────────────────────────────────────
  const [toProperties, setToProperties] = useState([]);
  const [toPropertyId, setToPropertyId] = useState('');
  const [toTenants, setToTenants] = useState([]);
  const [toTenantId, setToTenantId] = useState('');
  const [toUnitId, setToUnitId] = useState('');
  const [toParticular, setToParticular] = useState('rent');
  const [toCurrentBalance, setToCurrentBalance] = useState(0);

  // ── Reason & transfer state ────────────────────────────────────────────────
  const [reason, setReason] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);

  // ── Transfer panel collapse ───────────────────────────────────────────────
  const [transferOpen, setTransferOpen] = useState(true);

  // ── Log table + filters ───────────────────────────────────────────────────
  const [redirections, setRedirections] = useState([]);
  const [logDateFrom, setLogDateFrom] = useState('');
  const [logDateTo, setLogDateTo] = useState('');
  const [logParticular, setLogParticular] = useState('');

  const currency = 'KES';

  // ── Load properties once on mount ─────────────────────────────────────────
  useEffect(() => {
    loadProperties();
    loadRedirections();
  }, []);

  // ── When FROM property changes, load its tenants ──────────────────────────
  useEffect(() => {
    if (fromPropertyId) loadTenantsForProperty(fromPropertyId, 'from');
  }, [fromPropertyId]);

  // ── When TO property changes, load its tenants ────────────────────────────
  useEffect(() => {
    if (toPropertyId) loadTenantsForProperty(toPropertyId, 'to');
  }, [toPropertyId]);

  // ── When FROM tenant changes, reload payments for that tenant ─────────────
  useEffect(() => {
    if (fromTenantId) loadPaymentsForTenant(fromTenantId, fromPaymentDate);
  }, [fromTenantId]);

  // ── When TO tenant changes, look up their current balance ─────────────────
  useEffect(() => {
    if (toTenantId) loadTenantBalance(toTenantId);
  }, [toTenantId]);

  const loadProperties = async () => {
    try {
      let url;
      if (user.type === 'admin') {
        url = `${process.env.REACT_APP_API_URL}properties?where={"status":"active"}&take=1000&order={"name":"ASC"}`;
      } else {
        url = `${process.env.REACT_APP_API_URL}properties/byuser?status=active&user_id=${user.id}`;
      }
      const { data: { response } } = await axios.get(url, requestConfig);
      const list = Array.isArray(response) ? response : [response];
      setFromProperties(list);
      setToProperties(list);
      if (list.length > 0) {
        setFromPropertyId(list[0].id);
        setToPropertyId(list[0].id);
      }
    } catch (e) { /* ignore */ }
  };

  const loadTenantsForProperty = async (propertyId, direction) => {
    try {
      const unitsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}units?where={"property_id":"${propertyId}"}&take=10000`,
        requestConfig,
      );
      const unitList = unitsRes.data.response || [];
      const unitIds = unitList.map((u) => u.id).join(',');
      if (!unitIds) {
        if (direction === 'from') setFromTenants([]);
        else setToTenants([]);
        return;
      }
      const tenantsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}tenants?where={"unit_id":"in::${unitIds}","status":"active"}&take=10000`,
        requestConfig,
      );
      const tenantList = tenantsRes.data.response || [];
      if (direction === 'from') {
        setFromTenants(tenantList);
        if (tenantList.length > 0) setFromTenantId(tenantList[0].id);
      } else {
        setToTenants(tenantList);
        if (tenantList.length > 0) {
          setToTenantId(tenantList[0].id);
          setToUnitId(tenantList[0].unit_id);
        }
      }
    } catch (e) { /* ignore */ }
  };

  const loadPaymentsForTenant = async (tenantId, date) => {
    try {
      let whereClause = `{"tenant_id":"${tenantId}","status":"success"}`;
      let dateFilter = '';
      if (date) {
        const start = moment(date).startOf('day').toISOString();
        const end = moment(date).endOf('day').toISOString();
        dateFilter = `&from=${start}&to=${end}`;
      }
      const url = `${process.env.REACT_APP_API_URL}payments?where=${whereClause}&take=200&order={"createdat":"DESC"}${dateFilter}`;
      const { data: { response } } = await axios.get(url, requestConfig);
      const list = response || [];
      setFromPayments(list);
      if (list.length > 0) {
        setFromPaymentId(list[0].id);
        setFromPaymentAmount(list[0].amount);
      } else {
        setFromPaymentId('');
        setFromPaymentAmount(0);
      }
    } catch (e) { /* ignore */ }
  };

  const loadTenantBalance = async (tenantId) => {
    try {
      const { data: { response } } = await axios.get(
        `${process.env.REACT_APP_API_URL}tenants?where={"id":"${tenantId}"}&take=1`,
        requestConfig,
      );
      const t = Array.isArray(response) ? response[0] : response;
      setToCurrentBalance(t && t.balance != null ? t.balance : 0);
    } catch (e) { /* ignore */ }
  };

  const handleSearchPayments = () => {
    if (fromTenantId) loadPaymentsForTenant(fromTenantId, fromPaymentDate);
  };

  const handleFromPaymentSelect = (paymentId) => {
    setFromPaymentId(paymentId);
    const p = fromPayments.find((x) => x.id === paymentId);
    if (p) setFromPaymentAmount(p.amount);
  };

  const handleToTenantSelect = (tenantId) => {
    setToTenantId(tenantId);
    const t = toTenants.find((x) => x.id === tenantId);
    if (t) setToUnitId(t.unit_id);
  };

  const handleTransfer = async () => {
    if (!fromPaymentId || !toTenantId || !reason) {
      IziToast('Please select a source payment, destination tenant, and enter a reason');
      return;
    }
    if (fromTenantId === toTenantId) {
      IziToast('Source and destination tenant cannot be the same');
      return;
    }
    setIsTransferring(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}paymentredirections`,
        {
          from_payment_id: fromPaymentId,
          from_tenant_id: fromTenantId,
          to_tenant_id: toTenantId,
          to_unit_id: toUnitId,
          property_id: toPropertyId,
          particular: toParticular,
          reason,
        },
        requestConfig,
      );
      IziToast('Payment successfully transferred');
      setReason('');
      setFromPaymentId('');
      setFromPaymentAmount(0);
      setFromPayments([]);
      loadRedirections();
      loadTenantBalance(toTenantId);
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to transfer payment');
    }
    setIsTransferring(false);
  };

  const loadRedirections = async () => {
    try {
      const { data: { response } } = await axios.get(
        `${process.env.REACT_APP_API_URL}paymentredirections?take=500&order={"createdat":"DESC"}`,
        requestConfig,
      );
      setRedirections(response || []);
    } catch (e) {
      IziToast('Failed to load payment redirection log');
    }
  };

  const newBalance = toCurrentBalance - fromPaymentAmount;

  const particularLabel = (p) => {
    switch (p) {
      case 'rent': return 'Rent';
      case 'water': return 'Water';
      case 'service_charge': return 'Service Charge';
      case 'utilities': return 'Utilities';
      case 'charges': return 'Charges';
      case 'other': return 'Other';
      default: return p || '-';
    }
  };

  const tenantLabel = (t) => {
    const unit = t.other_details && t.other_details.unit_number
      ? `Unit ${t.other_details.unit_number}` : t.unit_id;
    return `${t.first_name} ${t.last_name} — ${unit}`;
  };

  // Apply log filters client-side
  const filteredRedirections = redirections.filter((r) => {
    if (logParticular && r.particular !== logParticular) return false;
    if (logDateFrom && moment(r.createdat).isBefore(moment(logDateFrom).startOf('day'))) return false;
    if (logDateTo && moment(r.createdat).isAfter(moment(logDateTo).endOf('day'))) return false;
    return true;
  });

  const clearLogFilters = () => {
    setLogDateFrom('');
    setLogDateTo('');
    setLogParticular('');
  };

  // ── CSV export of currently filtered rows ────────────────────────────────
  const exportCSV = () => {
    if (filteredRedirections.length === 0) {
      IziToast('No records to export');
      return;
    }
    const headers = [
      'No.', 'Date', 'Tenant From', 'Unit From',
      'Tenant To', 'Unit To', 'Amount', 'Particular',
      'Transfer By', 'Reason',
    ];
    const escape = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val).replace(/"/g, '""');
      return /[",\n\r]/.test(s) ? `"${s}"` : s;
    };
    const rows = filteredRedirections.map((rec, idx) => [
      idx + 1,
      moment(rec.createdat).format('YYYY-MM-DD HH:mm'),
      rec.from_suspense_id ? 'Suspense' : (rec.from_tenant_name || ''),
      rec.from_tenant_unit || '',
      rec.to_tenant_name || '',
      rec.to_tenant_unit || '',
      `${currency} ${(rec.amount || 0).toLocaleString(undefined, moneyOpts)}`,
      rec.particular ? particularLabel(rec.particular) : '',
      rec.redirected_by_name || '',
      rec.reason || '',
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payment-redirections-${moment().format('YYYY-MM-DD-HHmm')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── PDF export via browser print dialog ─────────────────────────────────
  const exportPDF = () => {
    if (filteredRedirections.length === 0) {
      IziToast('No records to export');
      return;
    }
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      IziToast('Please allow popups to export PDF');
      return;
    }
    const filterSummary = [
      logDateFrom ? `From: ${logDateFrom}` : null,
      logDateTo ? `To: ${logDateTo}` : null,
      logParticular ? `Particular: ${logParticular}` : null,
    ].filter(Boolean).join(' &nbsp;|&nbsp; ') || 'All records';

    const rowsHtml = filteredRedirections.map((rec, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${moment(rec.createdat).format('D/M/YYYY HH:mm')}</td>
        <td>${rec.from_suspense_id ? 'Suspense' : (rec.from_tenant_name || '-')}${rec.from_tenant_unit ? `<br/><small>Unit ${rec.from_tenant_unit}</small>` : ''}</td>
        <td>${rec.to_tenant_name || '-'}${rec.to_tenant_unit ? `<br/><small>Unit ${rec.to_tenant_unit}</small>` : ''}</td>
        <td>${currency} ${(rec.amount || 0).toLocaleString(undefined, moneyOpts)}</td>
        <td>${particularLabel(rec.particular)}</td>
        <td>${rec.redirected_by_name || '-'}</td>
        <td>${rec.reason || '-'}</td>
      </tr>
    `).join('');

    win.document.write(`
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Payment Redirection Log</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #333; }
          h2 { color: #c0392b; margin: 0 0 4px; }
          .meta { font-size: 12px; color: #777; margin-bottom: 18px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th { background: #c0392b; color: #fff; padding: 8px; text-align: left; }
          td { padding: 8px; border-bottom: 1px solid #eee; vertical-align: top; }
          tr:nth-child(even) td { background: #fafafa; }
          small { color: #888; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h2>Payment Redirection Log</h2>
        <div class="meta">
          Exported ${moment().format('D/M/YYYY HH:mm')} &nbsp;|&nbsp;
          ${filteredRedirections.length} record(s) &nbsp;|&nbsp;
          ${filterSummary}
        </div>
        <table>
          <thead>
            <tr>
              <th>No.</th><th>Date</th><th>Tenant From</th><th>Tenant To</th>
              <th>Amount</th><th>Particular</th><th>Transfer By</th><th>Reason</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function(){ window.close(); }, 500);
          };
        </script>
      </body>
      </html>
    `);
    win.document.close();
  };

  return (
    <Master redirectLink="paymentredirection" title="Finance" source="Payment Redirection">
      {/* ── Collapsible Transfer header bar ──────────────────────────────── */}
      <div
        onClick={() => setTransferOpen((v) => !v)}
        style={{
          cursor: 'pointer',
          background: transferOpen ? '#c0392b' : '#34495e',
          color: '#fff',
          padding: '12px 18px',
          borderRadius: 4,
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'background 0.2s',
          userSelect: 'none',
        }}
      >
        <span style={{ fontWeight: 'bold', fontSize: 14 }}>
          <i
            className={`fa ${transferOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`}
            style={{ marginRight: 10 }}
          />
          Transfer a Payment
        </span>
        <span style={{ fontSize: 12, opacity: 0.85 }}>
          {transferOpen ? 'Click to hide' : 'Click to show'}
        </span>
      </div>

      {transferOpen && (
      <div className="row">

        {/* ── LEFT COLUMN: Select source payment ── */}
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-arrow-circle-right" style={{ marginRight: 6 }} />
                Select Payment to Transfer (From)
              </h4>
            </div>
            <div className="panel-body">
              <div className="form-group">
                <label className="control-label">Property</label>
                <select
                  className="form-control"
                  value={fromPropertyId}
                  onChange={(e) => setFromPropertyId(e.target.value)}
                >
                  {fromProperties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="control-label">Tenant</label>
                <select
                  className="form-control"
                  value={fromTenantId}
                  onChange={(e) => setFromTenantId(e.target.value)}
                >
                  <option value="">-- Select tenant --</option>
                  {fromTenants.map((t) => (
                    <option key={t.id} value={t.id}>{tenantLabel(t)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="control-label">Payment Date (optional)</label>
                <div className="input-group">
                  <input
                    type="date"
                    className="form-control"
                    value={fromPaymentDate}
                    onChange={(e) => setFromPaymentDate(e.target.value)}
                  />
                  <span className="input-group-btn">
                    <button className="btn btn-default" type="button" onClick={handleSearchPayments}>
                      <i className="fa fa-search" />
                    </button>
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label className="control-label">Select Payment</label>
                <select
                  className="form-control"
                  value={fromPaymentId}
                  onChange={(e) => handleFromPaymentSelect(e.target.value)}
                >
                  <option value="">-- Select payment --</option>
                  {fromPayments.map((p) => (
                    <option key={p.id} value={p.id}>
                      {moment(p.createdat).format('D/M/YYYY')} &nbsp;|&nbsp;
                      {currency} {(p.amount || 0).toLocaleString(undefined, moneyOpts)} &nbsp;|&nbsp;
                      {p.reference_data && p.reference_data.payment_code
                        ? p.reference_data.payment_code : p.id.substring(0, 8)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="control-label">Payment Amount</label>
                <div
                  className="form-control"
                  style={{ background: '#f9f9f9', fontWeight: 'bold', color: '#c0392b' }}
                >
                  {currency} {fromPaymentAmount.toLocaleString(undefined, moneyOpts)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN: Select destination ── */}
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-arrow-circle-left" style={{ marginRight: 6 }} />
                Assign Payment To (Destination)
              </h4>
            </div>
            <div className="panel-body">
              <div className="form-group">
                <label className="control-label">Property</label>
                <select
                  className="form-control"
                  value={toPropertyId}
                  onChange={(e) => setToPropertyId(e.target.value)}
                >
                  {toProperties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="control-label">Tenant</label>
                <select
                  className="form-control"
                  value={toTenantId}
                  onChange={(e) => handleToTenantSelect(e.target.value)}
                >
                  <option value="">-- Select tenant --</option>
                  {toTenants.map((t) => (
                    <option key={t.id} value={t.id}>{tenantLabel(t)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="control-label">Particular</label>
                <select
                  className="form-control"
                  value={toParticular}
                  onChange={(e) => setToParticular(e.target.value)}
                >
                  <option value="rent">Rent</option>
                  <option value="water">Water</option>
                  <option value="service_charge">Service Charge</option>
                  <option value="utilities">Utilities</option>
                  <option value="charges">Charges</option>
                  <option value="other">Other</option>
                </select>
                <small style={{ color: '#888', display: 'block', marginTop: 4 }}>
                  Outstanding {toParticular === 'service_charge' ? 'service charge'
                    : toParticular} is settled first; any extra clears other
                  arrears, then stays as credit for future {toParticular === 'service_charge'
                    ? 'service charge' : toParticular}.
                </small>
              </div>
              <div className="form-group">
                <label className="control-label">Current Balance</label>
                <div
                  className="form-control"
                  style={{ background: '#f9f9f9', fontWeight: 'bold' }}
                >
                  {currency} {toCurrentBalance.toLocaleString(undefined, moneyOpts)}
                </div>
              </div>
              <div className="form-group">
                <label className="control-label">Amount to Credit</label>
                <div
                  className="form-control"
                  style={{ background: '#f9f9f9', fontWeight: 'bold', color: '#27ae60' }}
                >
                  {currency} {fromPaymentAmount.toLocaleString(undefined, moneyOpts)}
                </div>
              </div>
              <div className="form-group">
                <label className="control-label">New Balance (after transfer)</label>
                <div
                  className="form-control"
                  style={{
                    background: '#f9f9f9',
                    fontWeight: 'bold',
                    color: newBalance < 0 ? '#c0392b' : '#27ae60',
                  }}
                >
                  {currency} {newBalance.toLocaleString(undefined, moneyOpts)}
                </div>
              </div>
              <div className="form-group">
                <label className="control-label">Reason for Payment Redirection</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Payment made to wrong unit, duplicated entry..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
              <div className="text-right">
                <Button
                  caption={isTransferring ? 'Transferring...' : 'Transfer Payment'}
                  cssClass="btn-danger"
                  onClick={handleTransfer}
                  type="button"
                  isBlack={false}
                  disabled={isTransferring}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      )}

      {/* ── Payment Redirection Log ── */}
      <h4 style={{ marginTop: 24 }}>Payment Redirection Log</h4>

      {/* Filters */}
      <div
        className="row"
        style={{
          background: '#f8f9fa',
          padding: '12px 8px',
          borderRadius: 4,
          marginBottom: 14,
          marginLeft: 0,
          marginRight: 0,
        }}
      >
        <div className="col-md-3">
          <label className="control-label" style={{ fontSize: 12 }}>Date From</label>
          <input
            type="date"
            className="form-control"
            value={logDateFrom}
            onChange={(e) => setLogDateFrom(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="control-label" style={{ fontSize: 12 }}>Date To</label>
          <input
            type="date"
            className="form-control"
            value={logDateTo}
            onChange={(e) => setLogDateTo(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="control-label" style={{ fontSize: 12 }}>Particular</label>
          <select
            className="form-control"
            value={logParticular}
            onChange={(e) => setLogParticular(e.target.value)}
          >
            <option value="">All</option>
            <option value="rent">Rent</option>
            <option value="water">Water</option>
            <option value="service_charge">Service Charge</option>
            <option value="utilities">Utilities</option>
            <option value="charges">Charges</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="col-md-3" style={{ paddingTop: 22 }}>
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={clearLogFilters}
            style={{ marginRight: 4 }}
          >
            <i className="fa fa-times" style={{ marginRight: 4 }} />
            Clear
          </button>
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={exportCSV}
            style={{ marginRight: 4 }}
            title="Download filtered rows as CSV"
          >
            <i className="fa fa-file-text-o" style={{ marginRight: 4 }} />
            CSV
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={exportPDF}
            title="Open print dialog (Save as PDF)"
          >
            <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />
            PDF
          </button>
          <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>
            Showing <strong>{filteredRedirections.length}</strong> of {redirections.length}
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table id="redirectionsTable" className="table table-striped table-bordered">
          <thead>
            <tr>
              <th>No.</th>
              <th>Date</th>
              <th>Tenant From</th>
              <th>Tenant To</th>
              <th>Amount</th>
              <th>Particular</th>
              <th>Transfer By</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {filteredRedirections.map((rec, idx) => (
              <tr key={rec.id}>
                <td>{idx + 1}</td>
                <td>{moment(rec.createdat).format('D/M/YYYY - HH:mm')}</td>
                <td>
                  {rec.from_suspense_id ? (
                    <span className="label label-warning">Suspense</span>
                  ) : (
                    <span>
                      {rec.from_tenant_name || '-'}
                      {rec.from_tenant_unit && (
                        <small style={{ color: '#777', display: 'block' }}>
                          Unit {rec.from_tenant_unit}
                        </small>
                      )}
                    </span>
                  )}
                </td>
                <td>
                  {rec.to_tenant_name || '-'}
                  {rec.to_tenant_unit && (
                    <small style={{ color: '#777', display: 'block' }}>
                      Unit {rec.to_tenant_unit}
                    </small>
                  )}
                </td>
                <td>{currency} {(rec.amount || 0).toLocaleString(undefined, moneyOpts)}</td>
                <td>{particularLabel(rec.particular)}</td>
                <td>{rec.redirected_by_name || '-'}</td>
                <td>{rec.reason || '-'}</td>
              </tr>
            ))}
            {filteredRedirections.length === 0 && (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', color: '#888', padding: 20 }}>
                  No redirection records match the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Master>
  );
};

const mapStateToProps = (state) => state;
PaymentRedirection.propTypes = { user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]) };
PaymentRedirection.defaultProps = { user: {} };
export default connect(mapStateToProps)(PaymentRedirection);
